import { ENTITY_SLOTS, entityColorVar } from '@hoopoe/shared';
import { useId } from 'react';
import { cn } from '../../lib/cn';

/** Names of the entity palette slots (tokens.ts), so color is never the only cue. */
export const SLOT_NAMES = ['blue', 'orange', 'aqua', 'yellow', 'magenta', 'green', 'violet', 'red', 'cyan', 'brown', 'plum', 'olive'];
export const slotName = (slot: number | null | undefined) => (slot ? (SLOT_NAMES[slot - 1] ?? `color ${slot}`) : 'no color');

export function Swatch({ slot, className }: { slot: number | null | undefined; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block size-3.5 shrink-0 rounded-full border border-foreground/40', className)}
      style={{ background: entityColorVar(slot) }}
    />
  );
}

/**
 * Picks an entity palette slot (1–12) or none. Only palette colors exist in Hoopoe: admins choose a slot,
 * never a color value, so every color stays valid in light and dark mode.
 */
export function ColorSlotPicker({
  label,
  value,
  onChange,
  allowNone = true,
  noneLabel = 'None',
}: {
  label: string;
  value: number | null;
  onChange: (slot: number | null) => void;
  allowNone?: boolean;
  noneLabel?: string;
}) {
  const name = useId();
  const slots: (number | null)[] = [...(allowNone ? [null] : []), ...Array.from({ length: ENTITY_SLOTS }, (_, i) => i + 1)];
  return (
    <fieldset className="space-y-1">
      <legend className="text-sm font-medium">{label}</legend>
      <div className="flex flex-wrap gap-1">
        {slots.map(s => (
          <label
            key={s ?? 'none'}
            className="relative grid size-7 cursor-pointer place-items-center rounded-md border border-transparent has-[:checked]:border-foreground has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
          >
            <input
              type="radio"
              name={name}
              className="sr-only"
              checked={value === s}
              onChange={() => onChange(s)}
              aria-label={s ? `Color ${s} (${slotName(s)})` : noneLabel}
            />
            {s ? <Swatch slot={s} className="size-4" /> : <span className="text-xs text-muted-foreground">–</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
